import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { AdminShopSize, createShopSize, deleteShopSize, reorderShopSizes, updateShopSize } from "../../../api/shop";
import { Alert, Button, Card, Dialog, EmptyState, Field, IconButton, Input, Tag, useDialogs } from "../../../ui";
import { codeFromName, normalizeCode } from "../shared/codes";
import { GuardedDelete } from "../shared/GuardedDelete";
import { ReorderControls } from "../shared/ReorderControls";
import { useLatched } from "../shared/useLatched";
import { usePersistedOrder } from "../shared/useReorder";
import { usedByLabel, usedByText } from "./catalogGuards";
import { validateNamed } from "./catalogForms";
import type { CatalogTabProps } from "./types";

type SizeForm = { label: string; code: string; codeTouched: boolean };
const EMPTY: SizeForm = { label: "", code: "", codeTouched: false };

const SizesTab = ({ catalog, onChanged, showError, showSuccess }: CatalogTabProps) => {
  const { confirm } = useDialogs();
  // A saved order is read back into the page's catalog, so it holds after switching tabs
  const order = usePersistedOrder(
    catalog.sizes,
    (s) => s.id,
    reorderShopSizes,
    (err) => showError(err, "Failed to reorder sizes"),
    () => void onChanged()
  );
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AdminShopSize | null>(null);
  const [form, setForm] = useState<SizeForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const title = useLatched(open, editing ? "Edit Size" : "Add Size");
  const saveLabel = useLatched(open, editing ? "Update" : "Add");
  const problem = validateNamed(form.label, form.code, "label");

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setAttempted(false);
    setOpen(true);
  };

  const openEdit = (size: AdminShopSize) => {
    setEditing(size);
    setForm({ label: size.label, code: size.code, codeTouched: true });
    setAttempted(false);
    setOpen(true);
  };

  const close = () => {
    if (!saving) setOpen(false);
  };

  const handleSave = async () => {
    setAttempted(true);
    if (problem) return;
    setSaving(true);
    try {
      const body = { label: form.label.trim(), code: form.code };
      if (editing) {
        await updateShopSize(editing.id, body);
        showSuccess("Size updated successfully");
      } else {
        await createShopSize(body);
        showSuccess("Size added successfully");
      }
      setOpen(false);
      await onChanged();
    } catch (err: any) {
      showError(err, "Failed to save size");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (size: AdminShopSize) => {
    const ok = await confirm({
      title: "Delete Size",
      tone: "danger",
      confirmLabel: "Delete",
      message: (
        <>
          Are you sure you want to delete the size "<strong>{size.label}</strong>"?
        </>
      ),
    });
    if (!ok) return;
    try {
      await deleteShopSize(size.id);
      showSuccess("Size deleted successfully");
      await onChanged();
    } catch (err: any) {
      showError(err, "Failed to delete size");
    }
  };

  return (
    <>
      <Card
        padding="none"
        title="Sizes"
        subtitle="Every size a product can come in, in the order the shop lists them"
        actions={
          <Button size="sm" icon={<Plus size={16} />} onClick={openAdd}>
            Add Size
          </Button>
        }
      >
        {order.list.length === 0 ? (
          <EmptyState compact title="No sizes yet." description="Add the sizes your products come in: S, M, L, 42…" />
        ) : (
          <ul className="catalog-list">
            {order.list.map((size, index) => (
              <li
                key={size.id}
                className={`catalog-row${order.dragIndex === index ? " reorder-dragging" : ""}`}
                {...order.rowProps(index)}
              >
                <ReorderControls
                  index={index}
                  count={order.list.length}
                  onStep={order.step}
                  disabled={order.saving}
                  handleProps={order.handleProps(index)}
                />
                <div className="catalog-row__main">
                  <span className="catalog-row__name">{size.label}</span>
                  <Tag className="code-tag">{size.code}</Tag>
                </div>
                <span className="catalog-row__meta">{usedByLabel(size.productCount)}</span>
                <div className="catalog-row__actions">
                  <IconButton label="Edit" icon={<Pencil size={16} />} onClick={() => openEdit(size)} />
                  <GuardedDelete blockedReason={usedByText(size.productCount)} onDelete={() => handleDelete(size)} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Dialog
        open={open}
        onClose={close}
        title={title}
        size="sm"
        busy={saving}
        bodyClassName="catalog-form"
        footer={
          <>
            <Button variant="ghost" onClick={close} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving} disabled={!form.label.trim()}>
              {saveLabel}
            </Button>
          </>
        }
      >
        <Field label="Label" required hint="As customers see it: S, M, 42…">
          {(id) => (
            <Input
              id={id}
              value={form.label}
              maxLength={50}
              placeholder="e.g. M"
              onChange={(e) => {
                const label = e.target.value;
                setForm((f) => ({ ...f, label, code: f.codeTouched ? f.code : codeFromName(label) }));
              }}
            />
          )}
        </Field>
        <Field label="Code" required hint="1–6 letters or digits, used to build SKUs (PAD-TSH-01-BLK-M). Changing it never rewrites existing SKUs.">
          {(id) => (
            <Input
              id={id}
              value={form.code}
              maxLength={6}
              placeholder="e.g. M"
              onChange={(e) => {
                const code = normalizeCode(e.target.value);
                setForm((f) => ({ ...f, code, codeTouched: true }));
              }}
            />
          )}
        </Field>
        {attempted && problem && <Alert tone="danger">{problem}</Alert>}
      </Dialog>
    </>
  );
};

export default SizesTab;
